import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VendorSearchInput } from './VendorSearchInput';
import { UserSearchInput } from '@/components/tasks/UserSearchInput';
import { serviceService } from '@/lib/api/service.service';
import { eventService } from '@/lib/api/event.service';
import type { 
  Service, 
  ServiceType,
  Vendor 
} from '@/types/service.types';
import type { UserContact } from '@/types/user-contact.types';

interface ServiceFormProps {
  eventId: number;
  service?: Service;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

// Service types with their appliesTo configuration
const SERVICE_TYPES: { 
  value: ServiceType; 
  label: string; 
  appliesTo: 'venue' | 'team_member' | 'guest' 
}[] = [
  { value: 'catering', label: 'Catering', appliesTo: 'venue' },
  { value: 'photography', label: 'Photography', appliesTo: 'team_member' },
  { value: 'music', label: 'Music', appliesTo: 'venue' },
  { value: 'decoration', label: 'Decoration', appliesTo: 'venue' },
  { value: 'transportation', label: 'Transportation', appliesTo: 'guest' },
  { value: 'other', label: 'Other', appliesTo: 'venue' }
];

// Mock data - in real app, these would come from API
const VENUES = [
  { id: 1, name: 'Main Hall' },
  { id: 2, name: 'Garden Area' },
  { id: 3, name: 'Reception Hall' }
];


const GUESTS = [
  { id: 1, name: 'John Smith' },
  { id: 2, name: 'Jane Doe' },
  { id: 3, name: 'Mike Johnson' }
];

export const ServiceForm: React.FC<ServiceFormProps> = ({
  eventId,
  service,
  isOpen,
  onClose,
  onSave
}) => {
  const [loading, setLoading] = useState(false);
    const { t } = useTranslation(['services']);
  const [celebrationId, setCelebrationId] = useState<number | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [selectedManagedBy, setSelectedManagedBy] = useState<UserContact | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'catering' as ServiceType,
    budget: undefined as number | undefined,
    status: 'pending' as 'pending' | 'confirmed' | 'completed' | 'cancelled',
    notes: '',
    managed_by_id: undefined as number | undefined,
    applies_to_id: undefined as number | undefined
  });

  const selectedServiceType = SERVICE_TYPES.find(st => st.value === formData.type);

  // Fetch event details to get celebration ID
  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        const event = await eventService.getEvent(eventId);
        if (event.celebration_id) {
          setCelebrationId(event.celebration_id);
        }
      } catch (error) {
        console.error('Error fetching event details:', error);
      }
    };
    
    if (eventId) {
      fetchEventDetails();
    }
  }, [eventId]);

  useEffect(() => {
    if (isOpen) {
      if (service) {
        setFormData({
          name: service.name || '',
          type: service.type,
          budget: service.estimated_cost,
          status: service.status,
          notes: service.notes || '',
          managed_by_id: service.managed_by_id,
          applies_to_id: undefined
        });
        
        // Set vendor if available
        if (service.vendor) {
          setSelectedVendor(service.vendor);
        } else if (service.vendor_id) {
          // If we only have vendor_id, try to fetch vendor details
          serviceService.getVendor(service.vendor_id).then(vendor => {
            setSelectedVendor(vendor);
          }).catch(err => console.error('Error fetching vendor:', err));
        }
        
        // If managed_by_id exists, we can't easily fetch the user details
        // So we'll just leave it as null (user can select again if needed)
      } else {
        resetForm();
      }
    }
  }, [isOpen, service]);

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'catering',
      budget: undefined,
      status: 'pending',
      notes: '',
      managed_by_id: undefined,
      applies_to_id: undefined
    });
    setSelectedVendor(null);
    setSelectedManagedBy(null);
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Map our form data to the backend expected format
      const serviceData = {
        type: formData.type,
        name: `${SERVICE_TYPES.find(st => st.value === formData.type)?.label} Service`,
        estimated_cost: formData.budget,
        status: formData.status,
        notes: formData.notes,
        vendor_id: selectedVendor?.id,
        managed_by_id: selectedManagedBy?.id
      };
      
      if (service) {
        await serviceService.updateService(service.id, serviceData);
      } else {
        await serviceService.createService(eventId, serviceData);
      }
      
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving service:', error);
      alert(t('services:form.errors.saveService'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getAppliesOptions = () => {
    if (!selectedServiceType) return [];
    
    switch (selectedServiceType.appliesTo) {
      case 'venue':
        return VENUES;
      case 'team_member':
        // For team members, we'll use dynamic search instead of dropdown
        return [];
      case 'guest':
        return GUESTS;
      default:
        return [];
    }
  };

  const getAppliesLabel = () => {
    if (!selectedServiceType) return 'Applies To';
    
    switch (selectedServiceType.appliesTo) {
      case 'venue':
        return t('services:form.fields.appliesToVenue');
      case 'team_member':
        return t('services:form.fields.appliesToTeamMember');
      case 'guest':
        return t('services:form.fields.appliesToGuest');
      default:
        return 'Applies To';
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
      data-testid="service-form-modal"
    >
      <div 
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto relative z-10"
        onClick={(e) => e.stopPropagation()} // Prevent overlay clicks from bubbling
      >
        <Card 
          className="w-full" 
          data-testid="service-form-card"
        >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('services:form.modal.title')}</CardTitle>
              <CardDescription>
                {service ? t('services:form.descriptions.updateService') : t('services:form.descriptions.newService')}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="service-form-close-button">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="service-form">
            {/* Service Type */}
            <div>
              <Label htmlFor="type">{t('services:form.fields.serviceType')}</Label>
              <select
                id="type"
                data-testid="service-type-select"
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value as ServiceType)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">{t('services:form.placeholders.selectService')}</option>
                {SERVICE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {t(`services:form.types.${type.value}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Applies To - shown when service type is selected */}
            {formData.type && (
              <div>
                <Label htmlFor="applies_to">{getAppliesLabel()}</Label>
                <select
                  id="applies_to"
                  data-testid="service-applies-to-select"
                  value={formData.applies_to_id || ''}
                  onChange={(e) => handleChange('applies_to_id', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">{t('services:form.placeholders.select')}</option>
                  {getAppliesOptions().map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Managed By */}
            <div>
              <Label htmlFor="managed_by">{t('services:form.fields.managedBy')}</Label>
              <UserSearchInput
                data-testid="service-managed-by-input"
                selectedUser={selectedManagedBy}
                onUserSelect={(user) => {
                  setSelectedManagedBy(user);
                  handleChange('managed_by_id', user ? user.id : undefined);
                }}
                placeholder={t('services:form.placeholders.searchTeamMember')}
                className="mt-1"
                filterTeamMembers={true}
                celebrationId={celebrationId || undefined}
              />
              <p className="text-sm text-gray-500 mt-1">
                {t('services:form.helpers.managedBy')}
              </p>
            </div>

            {/* Vendor Search */}
            <VendorSearchInput
              data-testid="service-vendor-input"
              serviceType={formData.type}
              selectedVendor={selectedVendor}
              onVendorSelect={setSelectedVendor}
              placeholder="Search vendors by name..."
            />

            {/* Budget */}
            <div>
              <Label htmlFor="budget">Budget (Optional)</Label>
              <Input
                id="budget"
                data-testid="service-budget-input"
                type="number"
                step="0.01"
                min="0"
                value={formData.budget || ''}
                onChange={(e) => handleChange('budget', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="0.00"
              />
            </div>

            {/* Status */}
            <div>
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                data-testid="service-status-select"
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value as 'pending' | 'confirmed' | 'completed' | 'cancelled')}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="invited">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <textarea
                id="notes"
                name="description"
                data-testid="service-notes-textarea"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Additional notes and description..."
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md resize-none"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" disabled={loading} className="bg-green-500 hover:bg-green-600 text-white flex-1" data-testid="service-save-button">
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Service'}
              </Button>
              <Button type="button" variant="outline" onClick={onClose} className="bg-gray-500 hover:bg-gray-600 text-white flex-1" data-testid="service-cancel-button">
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};
